import { atom } from 'jotai';
import { useMemo } from 'react';
import { useInstanceId } from 'shared/src/hooks/useInstanceId';
import type { BeautyInfo } from 'shared/src/api/getMemberInfo/types';
import { BODY_SIZE_OPTIONS, MAX_SKIN_PROBLEMS } from '../constants';

export type MyPersonalizationFormState = {
  height: number | null;
  weight: number | null;
  top: number | null;
  bottom: number | null;
  shoes: number | null;
  skinTone: BeautyInfo | null;
  skinType: BeautyInfo | null;
  skinProblems: BeautyInfo[];
  checksPrivacyAgreement: boolean;
};

/**
 * 마운트 시 한 번 박히고 변경되지 않는 seed.
 * - isAlreadyAgreed: 동의 영역 노출 여부 판단용
 * - initialSkinProblemSnos: 변경 여부 비교용 스냅샷
 */
export type MyPersonalizationSeedState = {
  isAlreadyAgreed: boolean;
  initialSkinProblemSnos: number[];
};

const EMPTY_FORM: MyPersonalizationFormState = {
  height: null,
  weight: null,
  top: null,
  bottom: null,
  shoes: null,
  skinTone: null,
  skinType: null,
  skinProblems: [],
  checksPrivacyAgreement: true,
};

const EMPTY_SEED: MyPersonalizationSeedState = {
  isAlreadyAgreed: false,
  initialSkinProblemSnos: [],
};

/**
 * jotai default store에 instanceId 별 슬롯을 가진 map atom으로 보관.
 * Provider scope을 쓰지 않는 이유는 subtree의 다른 jotai 사용을 격리하지 않기 위함.
 *
 * 코드베이스 컨벤션: `selectedOptionsAtom` (GoodsDetail) /
 * `filterListStateAtom` (FilterList)와 동일 패턴.
 */
export const formMapAtom = atom<Record<string, MyPersonalizationFormState>>({});
export const seedMapAtom = atom<Record<string, MyPersonalizationSeedState>>({});

/**
 * 현재 instanceId 슬롯을 derive해서 atom 묶음으로 반환.
 * 컴포넌트는 필요한 atom을 deconstruct해서 useAtomValue / useSetAtom으로 구독.
 */
export const useMyPersonalizationForm = () => {
  const instanceId = useInstanceId();

  return useMemo(() => {
    const formAtom = atom(
      get => get(formMapAtom)[instanceId] ?? EMPTY_FORM,
      (
        get,
        set,
        next:
          | MyPersonalizationFormState
          | ((prev: MyPersonalizationFormState) => MyPersonalizationFormState),
      ) => {
        const current = get(formMapAtom)[instanceId] ?? EMPTY_FORM;
        const value = typeof next === 'function' ? next(current) : next;
        set(formMapAtom, prev => ({ ...prev, [instanceId]: value }));
      },
    );

    const seedAtom = atom(
      get => get(seedMapAtom)[instanceId] ?? EMPTY_SEED,
    );

    const topLabelAtom = atom(get => {
      const code = get(formAtom).top;
      return BODY_SIZE_OPTIONS.find(o => o.code === code)?.label ?? null;
    });

    const emptyPersonalizationInfoAtom = atom(get => {
      const f = get(formAtom);
      return (
        f.height == null &&
        f.weight == null &&
        f.top == null &&
        f.bottom == null &&
        f.shoes == null &&
        f.skinTone == null &&
        f.skinType == null &&
        f.skinProblems.length === 0
      );
    });

    const showPrivacyAgreementAtom = atom(
      get =>
        !get(emptyPersonalizationInfoAtom) && !get(seedAtom).isAlreadyAgreed,
    );

    const isSubmitEnabledAtom = atom(
      get =>
        !get(showPrivacyAgreementAtom) || get(formAtom).checksPrivacyAgreement,
    );

    const skinProblemChangedAtom = atom(get => {
      const current = [...get(formAtom).skinProblems.map(p => p.sno)].sort(
        (a, b) => a - b,
      );
      const initial = get(seedAtom).initialSkinProblemSnos;
      if (current.length !== initial.length) return true;
      return current.some((sno, i) => sno !== initial[i]);
    });

    const toggleSkinProblemAtom = atom(
      null,
      (get, set, category: BeautyInfo | null) => {
        if (category == null) {
          set(formAtom, prev => ({ ...prev, skinProblems: [] }));
          return;
        }
        const current = get(formAtom).skinProblems;
        const exists = current.some(p => p.sno === category.sno);
        if (!exists && current.length >= MAX_SKIN_PROBLEMS) return;
        set(formAtom, prev => ({
          ...prev,
          skinProblems: exists
            ? current.filter(p => p.sno !== category.sno)
            : [...current, category],
        }));
      },
    );

    const togglePrivacyAgreementAtom = atom(null, (_, set) => {
      set(formAtom, prev => ({
        ...prev,
        checksPrivacyAgreement: !prev.checksPrivacyAgreement,
      }));
    });

    return {
      formAtom,
      seedAtom,
      topLabelAtom,
      emptyPersonalizationInfoAtom,
      showPrivacyAgreementAtom,
      isSubmitEnabledAtom,
      skinProblemChangedAtom,
      toggleSkinProblemAtom,
      togglePrivacyAgreementAtom,
    };
  }, [instanceId]);
};
